import { monaco } from 'react-monaco-editor';

interface CompletionItemLabel {
  description?: string;
  detail?: string;
  label: string;
}

interface MarkdownString {
  value: string;
  supportHtml?: boolean;
}

interface CompletionItem {
  insertText: string;
  label: CompletionItemLabel;
  kind: number;
  detail: string;
  documentation: MarkdownString;
}

interface CompletionItems {
  completionItems: CompletionItem[];
}

interface Hover {
  contents: MarkdownString[];
}

interface Response {
  result: any;
  error?: string;
}

export interface TableStructure {
  tables: WasmTableDef[];
  databases: WasmDatabaseDef[];
}

export type WasmTableStructure = {
  tables: { [tableName: string]: WasmTableDef };
  databases: { [databaseName: string]: WasmDatabaseDef };
};

export type WasmDatabaseDef = {
  name: string;
  description: string;
};

export type WasmTableDef = {
  name: string;
  displayName: string;
  description: string;
  columns: WasmColumnDef[];
};

export type WasmColumnDef = {
  name: string;
  displayName: string;
  description: string;
  type: string;
};

interface Cursor {
  line: number;
  column: number;
}

export type InlayHints = {
  inlayHints: InlayHint[];
};

export type InlayHint = {
  label: string;
  position: Cursor;
  kind: monaco.languages.InlayHintKind;
};

type QueryDatabaseTableInfo = {
  databases: string[];
  tables: string[];
  error: string;
};

export type QueryTimeInfo = {
  startTime: string;
  endTime: string;
  error: string;
};

export type QueryInfo = {
  databaseTableInfo: QueryDatabaseTableInfo;
  timeInfo: QueryTimeInfo;
};

export type SignatureHelp = {
  activeSignature: number;
  activeParameter: number;
  signatures: SignatureInformation[];
};

export type SignatureInformation = {
  documentation: string;
  label: string;
  parameters: ParameterInformation[];
};

export type ParameterInformation = {
  documentation: string;
  label: string;
};

export type VisualizeAnnotation = {
  name: string;
  value: string;
};

export function pfAutocomplete(input: string, cursor: Cursor): CompletionItems {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfAutocomplete) {
    return { completionItems: [] };
  }
  let s: string;
  try {
    s = window.pfAutocomplete(input, JSON.stringify(cursor));
  } catch {
    return { completionItems: [] };
  }
  if (!s) {
    return { completionItems: [] };
  }

  const resp: Response = JSON.parse(s);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result as CompletionItems;
}

export function pfAutocompleteRegisterTables(tables: TableStructure) {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfAutocompleteRegisterTables) {
    return;
  }
  let s: string;
  try {
    s = window.pfAutocompleteRegisterTables(JSON.stringify(tables));
  } catch {
    return;
  }
  if (!s) {
    return;
  }

  const resp: { error?: string } = JSON.parse(s);
  if (resp.error) {
    throw new Error(resp.error);
  }
}

export function pfInlayHints(input: string): InlayHints {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfInlayHints) {
    return { inlayHints: [] };
  }
  let s: string;
  try {
    s = window.pfInlayHints(input);
  } catch {
    return { inlayHints: [] };
  }
  if (!s) {
    return { inlayHints: [] };
  }

  const result: InlayHints = JSON.parse(s);
  if (!result.inlayHints) {
    result.inlayHints = [];
  }
  return result;
}

export function pfHover(input: string, cursor: Cursor): Hover {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfHover) {
    return { contents: [] };
  }
  let s: string;
  try {
    s = window.pfHover(input, JSON.stringify(cursor));
  } catch {
    return { contents: [] };
  }
  if (!s) {
    return { contents: [] };
  }

  const resp: Response = JSON.parse(s);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result;
}

export function pfGetQueryInfo(input: string): QueryInfo {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfGetQueryInfo) {
    throw new Error('not loaded yet');
  }
  const s = window.pfGetQueryInfo(input);
  if (!s) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(s);
  const result = (resp.result as QueryInfo) ?? {
    databaseTableInfo: {
      databases: [],
      tables: [],
      error: undefined,
    },
    timeInfo: {
      startTime: '',
      endTime: '',
      error: undefined,
    },
  };
  if (resp.error) {
    result.databaseTableInfo.error = resp.error;
  }
  if (result.databaseTableInfo.databases === null) {
    result.databaseTableInfo.databases = [];
  }
  if (result.databaseTableInfo.tables === null) {
    result.databaseTableInfo.tables = [];
  }
  return result;
}

export function pfSignatureHelp(input: string, cursor: Cursor): SignatureHelp {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfSignatureHelp) {
    return { signatures: [] } as SignatureHelp;
  }

  let s: string;
  try {
    s = window.pfSignatureHelp(input, JSON.stringify(cursor));
  } catch {
    return { signatures: [] } as SignatureHelp;
  }
  if (!s) {
    return { signatures: [] } as SignatureHelp;
  }

  const resp: Response = JSON.parse(s);
  if (resp.error) {
    // ignore errors and just proceed without showing any sig help
    return { signatures: [] } as SignatureHelp;
  }
  return resp.result;
}

export function pfGetVisualize(input: string): VisualizeAnnotation[] {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfGetVisualize) {
    throw new Error('not loaded yet');
  }
  const s = window.pfGetVisualize(input);
  if (!s) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(s);
  const result = (resp.result as VisualizeAnnotation[]) ?? [];
  if (resp.error) {
    // TODO handle error
  }
  return result;
}

export function pfSetVisualize(input: string, annotations: VisualizeAnnotation[]): string {
  // if wasm hasn't loaded yet, this function doesn't exist

  if (!window.pfSetVisualize) {
    throw new Error('not loaded yet');
  }
  const s = window.pfSetVisualize(input, JSON.stringify(annotations));
  if (!s) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(s);
  const result = (resp.result as string) ?? '';
  if (resp.error) {
    // TODO handle error
  }
  return result;
}

type PfUpdateQueryTablesAndTimeProps = {
  input: string;
  databases: string[];
  tables: string[];
  startTime: string;
  endTime: string;
};

export enum PfUpdateErroringComponentTypeEnum {
  TIME = 'time',
  DATABASE = 'database',
  TABLE = 'table',
  OTHER = 'other',
}

type PfUpdateQueryTablesAndTimeResponse = {
  error?: string;
  result: string;
  erroringComponent?: PfUpdateErroringComponentTypeEnum;
};

export function pfUpdateQueryTablesAndTime({
  input,
  databases,
  tables,
  startTime,
  endTime,
}: PfUpdateQueryTablesAndTimeProps): PfUpdateQueryTablesAndTimeResponse {
  // if wasm hasnt loaded yet, this function doesnt exist
  if (!window.pfUpdateQueryTablesAndTime) {
    return { result: '' };
  }

  const s = window.pfUpdateQueryTablesAndTime(
    input,
    JSON.stringify(databases),
    JSON.stringify(tables),
    JSON.stringify(startTime),
    JSON.stringify(endTime),
  );
  if (!s) {
    return { result: '' };
  }

  const resp: PfUpdateQueryTablesAndTimeResponse = JSON.parse(s);
  return resp;
}

type PfFilterParamNameProps = {
  path: string[];
  otherParams: string[];
};

export function pfFilterParamName({ path, otherParams }: PfFilterParamNameProps): string {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfFilterParamName) {
    throw new Error('not loaded yet');
  }
  const s = window.pfFilterParamName(JSON.stringify(path), JSON.stringify(otherParams));
  if (!s) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(s);
  if (resp.error) {
    throw new Error(resp.error);
  }

  return resp.result;
}

type PfFilterParamStatementProps = {
  paramName: string;
  propertyType: string;
  values: string[];
};

type PfParameter = {
  name: string;
  statement: string;
};

export function pfFilterParamStatement({
  paramName,
  propertyType,
  values,
}: PfFilterParamStatementProps): PfParameter {
  const propertyTypeForWasm = propertyType.startsWith('array-')
    ? propertyType.split('array-')[1]
    : propertyType;
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfFilterParamStatement) {
    throw new Error('not loaded yet');
  }
  const s = window.pfFilterParamStatement(paramName, propertyTypeForWasm, JSON.stringify(values));
  if (!s) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(s);
  if (resp.error) {
    throw new Error(resp.error);
  }

  return resp.result;
}

export type PfInsertDashboardInputProps = {
  paramName: string;
  paramType: string;
  path: string[];
  query: string;
};

export function pfInsertDashboardInput({
  paramName,
  paramType,
  path,
  query,
}: PfInsertDashboardInputProps): string {
  const paramTypeForWasm = paramType.startsWith('array-') ? 'array' : paramType;
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfInsertDashboardInput) {
    throw new Error('not loaded yet');
  }
  const newQuery = window.pfInsertDashboardInput(
    paramName,
    paramTypeForWasm,
    JSON.stringify(path),
    query,
  );
  if (!newQuery) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(newQuery);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result;
}

export type PfRemoveDashboardInputProps = {
  paramName: string;
  paramType: string;
  path: string[];
  query: string;
};

export function pfRemoveDashboardInput({
  paramName,
  paramType,
  path,
  query,
}: PfRemoveDashboardInputProps): string {
  const paramTypeForWasm = paramType.startsWith('array-') ? 'array' : paramType;
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfRemoveDashboardInput) {
    throw new Error('not loaded yet');
  }
  const newQuery = window.pfRemoveDashboardInput(
    paramName,
    paramTypeForWasm,
    JSON.stringify(path),
    query,
  );
  if (!newQuery) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(newQuery);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result;
}

type PfInlineParametersProps = {
  query: string;
  parameters: PfParameter[];
};

export function pfInlineParameters({ query, parameters }: PfInlineParametersProps): string {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfInlineParameters) {
    throw new Error('not loaded yet');
  }
  const newQuery = window.pfInlineParameters(query, JSON.stringify(parameters));
  if (!newQuery) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(newQuery);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result;
}

export type PfGetAllTablesInQuery = {
  query: string;
};

export type PfGetAllTablesInQueryResponse = {
  Database: string;
  Table: string;
};

export function pfGetAllTablesInQuery({
  query,
}: PfGetAllTablesInQuery): PfGetAllTablesInQueryResponse[] {
  // if wasm hasn't loaded yet, this function doesn't exist
  if (!window.pfGetAllTablesInQuery) {
    throw new Error('not loaded yet');
  }
  const result = window.pfGetAllTablesInQuery(query);
  if (!result) {
    throw new Error('no response');
  }

  const resp: Response = JSON.parse(result);
  if (resp.error) {
    throw new Error(resp.error);
  }
  return resp.result;
}
